import { Team, User } from '@tassomutuo-backoffice/types';

export interface CSVExportData {
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Converte un array di oggetti in formato CSV
 */
export const convertToCSV = (data: CSVExportData[]): string => {
  if (data.length === 0) return '';

  // Estrae le intestazioni dalle chiavi del primo oggetto
  const headers = Object.keys(data[0]);

  // Crea la riga di intestazioni
  const csvHeaders = headers.join(',');

  // Converte ogni riga di dati
  const csvRows = data.map((row) =>
    headers
      .map((header) => {
        const value = row[header];
        // Gestisce valori null/undefined
        if (value === null || value === undefined) return '';
        // Escapa le virgolette e racchiude in virgolette se contiene virgole o virgolette
        const stringValue = String(value);
        if (
          stringValue.includes(',') ||
          stringValue.includes('"') ||
          stringValue.includes('\n')
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      })
      .join(',')
  );

  return [csvHeaders, ...csvRows].join('\n');
};

/**
 * Scarica un file CSV
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Converte i dati degli utenti per l'export CSV
 */
export const prepareUsersDataForCSV = (
  users: User[],
  teams: Team[]
): CSVExportData[] => {
  console.log({ users });
  return users.map((user) => {
    let teamLabel = '';
    // Se l'utente è un team manager (user.teamManager)
    if (user.team) {
      teamLabel = `Team di ${user.name || 'N/A'}`;
    } else {
      // Trova il team dell'utente
      const userTeam = teams.find((t) =>
        t.members?.some((member) => member.id === user.id)
      );
      teamLabel = userTeam
        ? `Team di ${userTeam.teamManager?.name || 'N/A'}`
        : '';
    }

    // Prepara i CAP
    const caps =
      [
        user.profileCollaboratori?.CapUfficio,
        user.profileCollaboratori?.Cap2,
        user.profileCollaboratori?.Cap3,
        user.profileCollaboratori?.Cap4,
        user.profileCollaboratori?.Cap5,
      ]
        .filter(Boolean)
        .join(', ') || 'Nessun CAP';
    return {
      Email: user.email || '',
      Nome: user.name || '',
      Telefono: user.telephoneNumber || '',
      CAP: caps,
      Regione: user.profileCollaboratori?.Regione || '',
      Provincia: user.profileCollaboratori?.ProvinciaUfficio || '',
      'Data di creazione': user.createdAt
        ? new Date(user.createdAt).toLocaleDateString('it-IT')
        : '',
      'Team Manager': user.team ? 'Si' : 'No',
      Team: teamLabel,
      'Assegna Lead': user.canReceiveLeads ? 'Si' : 'No',
      Rating: user.bqWeeklyPerformance?.rating || '-',
      Performance: user.bqWeeklyPerformance?.perf_overall || '-',
      'Cr calc': user.bqWeeklyPerformance?.cr_calc || '-',
      Workload: user.bqWeeklyPerformance?.workload || '-',
      'Bq Integrity': user.bqIntegrityCheck?.isConsistent ? 'Si' : 'No',
      'Leads Ricevute': user.bqWeeklyPerformance?.received || 0,
    };
  });
};

/**
 * Esporta i dati degli utenti in CSV
 */
export const exportUsersToCSV = (
  users: User[],
  teams: Team[],
  filename?: string
): void => {
  const csvData = prepareUsersDataForCSV(users, teams);
  const csvContent = convertToCSV(csvData);
  const defaultFilename = `users_export_${
    new Date().toISOString().split('T')[0]
  }.csv`;
  downloadCSV(csvContent, filename || defaultFilename);
};
